/**
 * SEO utilities — useSEO lives in @/hooks/useSEO; shared constants and JSON-LD helpers here.
 */
import { useEffect } from 'react';
import type { Classified } from '@shopgnd/shared';
import { buildClassifiedStructuredData } from '@shopgnd/shared';
import { classifiedPath } from '@/lib/classifiedUrl';
import { propertyPath } from '@/lib/propertyUrl';

export {
  SITE_NAME,
  BASE_URL,
  INSTAGRAM_URL,
  SEO_KEYWORDS,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_IMAGE,
  HOME_TITLE,
  MARKETPLACE_TAGLINE,
  MARKETPLACE_TITLE_PHRASE,
  MES_SLUG,
} from '@/lib/seo/constants';

export { getSiteSocialLinks, siteSocialSameAs } from '@/lib/siteSocialLinks';

export { useSEO } from '@/hooks/useSEO';
export type { SEOConfig, LegacySEOOptions } from '@/hooks/useSEO';

export {
  buildOrganizationSchema,
  buildLocalBusinessSchema,
  buildSolarCompanySchema,
  buildMESSchema,
  buildListingSchema,
  buildPropertyListingSchema,
  buildFAQSchema,
  buildServiceSchema,
  buildItemListSchema,
  buildImageObjectSchema,
  MES_FAQS,
} from '@/lib/seo/schemas';
export {
  buildBreadcrumbSchema,
  hubBreadcrumbs,
  productBreadcrumbs,
  businessBreadcrumbs,
  classifiedBreadcrumbs,
  classifiedHubBreadcrumbs,
  propertyBreadcrumbs,
  solarCompanyBreadcrumbs,
  parishBreadcrumbs,
  HOME_CRUMB,
  type BreadcrumbCrumb,
} from '@/lib/seo/breadcrumbs';

import siteStructuredDataJson from '../../seo/site-structured-data.json';
import { runtimeSiteSocialSameAs } from '@/lib/platformSiteSocial';
import { BASE_URL, DEFAULT_IMAGE, DEFAULT_DESCRIPTION, INSTAGRAM_URL, SITE_NAME } from '@/lib/seo/constants';

/** Turn relative paths into absolute URLs for OG/Twitter images. */
export function absoluteUrl(path: string): string {
  if (!path) return DEFAULT_IMAGE;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** @deprecated Schema injection is handled by useSEO({ schema }). Kept for gradual migration. */
export function useJsonLd(data: Record<string, unknown> | null) {
  useEffect(() => {
    if (!data) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-schema', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);
}

/** Site-wide Organization + WebSite graph (canonical copy in seo/site-structured-data.json). */
export function siteStructuredData(): Record<string, unknown> {
  return siteStructuredDataJson as Record<string, unknown>;
}

/** Organization + WebSite graph with live sameAs from platform settings. */
export function siteStructuredDataForPage(): Record<string, unknown> {
  const sameAs = runtimeSiteSocialSameAs();
  const graph = (siteStructuredDataJson as { '@graph'?: Record<string, unknown>[] })['@graph'] ?? [];
  return {
    '@context': 'https://schema.org',
    '@graph': graph.map((node) =>
      node['@type'] === 'Organization' ? { ...node, sameAs: sameAs.length ? sameAs : node.sameAs } : node,
    ),
  };
}

export function propertyJsonLd(c: Classified): Record<string, unknown> {
  const pageUrl = `${BASE_URL}${propertyPath(c)}/`;
  return buildClassifiedStructuredData(c, { pageUrl, baseUrl: BASE_URL });
}

export function classifiedJsonLd(c: Classified): Record<string, unknown> {
  const pagePath = c.category === 'property' ? propertyPath(c) : classifiedPath(c);
  const pageUrl = `${BASE_URL}${pagePath}/`;
  return buildClassifiedStructuredData(c, { pageUrl, baseUrl: BASE_URL });
}
